// Grammar Units 10 Data - Unit 10: Participles Complete Learning Course
// Based on Ch10 (1st Half).jsx and Ch10 (2nd Half).jsx
// Organized for Supabase migration and systematic data management

export const grammarUnits10Data = [
  {
    id: 10,
    title: { traditional: '分詞完全學習課程', simplified: '分词完全学习课程', english: 'Participles Complete Learning Course' },
    subtitle: { traditional: '掌握分詞的各種用法', simplified: '掌握分词的各种用法', english: 'Master various uses of participles' },
    description: { traditional: '系統性學習英文分詞，包括現在分詞、過去分詞、複合形容詞和特殊用法。', simplified: '系统性学习英文分词，包括现在分词、过去分词、复合形容词和特殊用法。', english: 'Systematic learning of English participles including present participles, past participles, compound adjectives and special usage.' },
    icon: '🎯',
    color: 'from-orange-500 to-red-600',
    status: 'notStarted',
    difficulty: 'intermediate',
    estimatedHours: 4,
    content: {
      overview: {
        title: "分詞 (Participles) Overview",
        content: {
          types: [
            { type: "V-ing", name: "現在分詞", meaning: "表「主動、進行」", example: "amazing, boring, exciting" },
            { type: "p.p.", name: "過去分詞", meaning: "表「被動、完成」", example: "amazed, bored, excited" }
          ],
          basicVerbs: [
            { base: "amaze", ving: "amazing", pp: "amazed" },
            { base: "bore", ving: "boring", pp: "bored" },
            { base: "embarrass", ving: "embarrassing", pp: "embarrassed" },
            { base: "excite", ving: "exciting", pp: "excited" },
            { base: "frighten", ving: "frightening", pp: "frightened" },
            { base: "please", ving: "pleasing", pp: "pleased" },
            { base: "surprise", ving: "surprising", pp: "surprised" },
            { base: "annoy", ving: "annoying", pp: "annoyed" },
            { base: "confuse", ving: "confusing", pp: "confused" },
            { base: "interest", ving: "interesting", pp: "interested" },
            { base: "tire", ving: "tiring", pp: "tired" }
          ]
        }
      },
      usage1: {
        title: "1. 分詞搭配動詞使用",
        content: {
          description: "現在分詞和過去分詞具有動詞的部分性質，其差異和功能見下列表格：",
          comparison: {
            ving: {
              function: "敘述現在的情況，表主動、進行",
              formation: "由「動詞 + -ing」構成",
              withBe: "be + V-ing 構成進行式",
              example: "The dog is chasing the postman. 那條狗正在追郵差。"
            },
            pp: {
              function: "敘述已經發生的事情，表被動、完成",
              formation: "規則變化由「動詞 + -ed」構成",
              withBe: "be + p.p. 構成被動式",
              withHave: "have + p.p. 構成完成式",
              examples: [
                "The mailman was chased by the dog. 郵差被狗追。",
                "The dog has chased the mailman. 那條狗追過郵差。"
              ]
            }
          }
        }
      },
      usage2: {
        title: "2. 分詞作形容詞用",
        content: {
          description: "現在分詞和過去分詞皆可作形容詞用，用來修飾名詞，其中修飾名詞的方式有兩種，分別是前位修飾與後位修飾。",
          positions: {
            before: {
              title: "前位修飾 (形容詞 + 名詞)",
              examples: [
                { ving: "a laughing woman", translation: "一位微笑的女人" },
                { ving: "a galloping horse", translation: "一匹疾馳的馬" },
                { ving: "boiling water", translation: "正在煮沸的水" },
                { pp: "a wounded soldier", translation: "一名受傷的軍人" },
                { pp: "a ruined country", translation: "一個被摧毀的國家" },
                { pp: "boiled water", translation: "煮開的水" }
              ]
            },
            after: {
              title: "後位修飾 (名詞 + 形容詞)",
              examples: [
                { ving: "a girl dancing to the music", translation: "一個隨音樂起舞的女孩" },
                { ving: "a boy running after his dog", translation: "一個追著狗跑的男孩" },
                { pp: "a letter written in English", translation: "一封用英文寫的信" },
                { pp: "a mother loved by her children", translation: "一位受兒女愛戴的母親" }
              ]
            }
          },
          feelingsUsage: {
            ving: {
              title: "現在分詞 (V-ing)",
              meaning: "表示「令人覺得......的」，描述某人或某事引起的感受",
              examples: [
                "Soaking in a hot spring is very relaxing. 泡溫泉讓人覺得很放鬆。",
                "The horror movie was very frightening. 這部恐怖電影很嚇人。"
              ]
            },
            pp: {
              title: "過去分詞 (p.p.)",
              meaning: "表示「感到、覺得......的」，描述某人所經歷的感受",
              examples: [
                "After going on holiday, Todd is now very relaxed. 度過假期後，現在感覺很放鬆。",
                "Alicia was frightened after watching the horror movie. 愛莉西亞看完恐怖電影後被嚇到了。"
              ]
            }
          }
        }
      },
      usage3: {
        title: "3. 分詞作補語用",
        content: {
          description: "現在分詞指「動作持續、主動」時，過去分詞表示「感到......、被動」",
          subjectComplement: {
            title: "3-1 分詞作主詞補語",
            verbs: ["be", "remain", "stay", "keep", "go", "come", "sit", "stand"],
            pattern: "S. + 動詞 + V-ing/p.p.",
            examples: [
              "This story is very exciting. 這個故事令人很興奮。",
              "The old lady sat surrounded by her grandchildren. 老太太被她的孫子們圍繞著。",
              "The two boys stood holding signs. 這兩個男孩握著標語站著。"
            ]
          },
          objectComplement: {
            title: "3-2 分詞作受詞補語",
            patterns: [
              {
                pattern: "S. + find + O. + V-ing(主動)/p.p.(被動)",
                meaning: "發現(某人某物)......",
                examples: [
                  "Robbie came home late and found his father waiting for him.",
                  "The refugees tried to return home, only to find their homes destroyed."
                ]
              },
              {
                pattern: "S. + keep + O. + V-ing(主動)/p.p.(被動)/adj.",
                meaning: "使......處於......狀態中",
                examples: [
                  "Don't keep me waiting too long.",
                  "John hired a maid to keep his house clean."
                ]
              },
              {
                pattern: "S. + leave + O. + V-ing(主動)/p.p.(被動)/adj.",
                meaning: "任由......處於......狀態中",
                examples: [
                  "Mark left his car running while he went into the shop.",
                  "Tina always leaves her shoelaces undone."
                ]
              }
            ]
          }
        }
      },
      compounds: {
        title: "4. 複合形容詞的構成",
        content: {
          activeCompounds: {
            title: "4-1 具有主動意味的複合形容詞",
            patterns: [
              {
                type: "名詞 + 現在分詞",
                examples: [
                  { compound: "a prize-winning author", meaning: "= an author that/who has won a prize" },
                  { compound: "a cat-loving friend", meaning: "= a friend who loves cats" }
                ]
              },
              {
                type: "形容詞 + 現在分詞", 
                examples: [
                  { compound: "a good-looking man", meaning: "= a man that/who looks good" },
                  { compound: "a sweet-tasting cake", meaning: "= a cake that tastes sweet" }
                ]
              },
              {
                type: "副詞 + 現在分詞",
                examples: [
                  { compound: "a fast-flowing river", meaning: "= a river that flows fast" },
                  { compound: "a fast-spreading fire", meaning: "= a fire that spreads fast" }
                ]
              }
            ]
          },
          passiveCompounds: {
            title: "4-2 具有被動意味的複合形容詞",
            patterns: [
              {
                type: "名詞 + 過去分詞",
                examples: [
                  { compound: "a moth-eaten sweater", meaning: "= a sweater that has been eaten into by moths" },
                  { compound: "a test-oriented education", meaning: "= an education that is oriented toward tests" }
                ]
              },
              {
                type: "形容詞 + 過去分詞",
                examples: [
                  { compound: "a light-colored blouse", meaning: "= a blouse that has a light color" },
                  { compound: "a well-educated woman", meaning: "= a woman that/who is well educated" }
                ]
              },
              {
                type: "副詞 + 過去分詞",
                examples: [
                  { compound: "a well-balanced diet", meaning: "= a diet that is balanced well" },
                  { compound: "a plain-spoken man", meaning: "= a man who speaks plainly" }
                ]
              }
            ]
          }
        }
      },
      memory_tips: {
        title: "記憶技巧",
        tips: [
          "記憶口訣：V-ing表主動進行，p.p.表被動完成",
          "視覺記憶：-ing像進行中的波浪～～～，-ed像已完成的方塊■",
          "be + V-ing = 正在做某事（進行式）",
          "be + p.p. = 被某人做（被動式）",
          "have + p.p. = 已經做完（完成式）",
          "V-ing修飾：令人感到...(物→人的感受)",
          "p.p.修飾：自己感到...(人的感受)",
          "前位修飾：形容詞放名詞前",
          "後位修飾：形容詞放名詞後，通常較長",
          "find/keep/leave + 受詞 + 分詞 = 讓某人/某物處於某狀態",
          "主動用V-ing，被動用p.p.",
          "複合形容詞 = 兩個詞合成一個形容詞",
          "主動複合：名詞/形容詞/副詞 + V-ing",
          "被動複合：名詞/形容詞/副詞 + p.p."
        ]
      },
      exercises: {
        title: "練習題",
        sections: [
          {
            name: "分詞練習 1",
            questions: [
              {
                question: "1. The movie was very __________.",
                options: {
                  A: "excite",
                  B: "exciting", 
                  C: "excited",
                  D: "excitement"
                },
                answer: "B",
                explanation: "exciting 是現在分詞，表示「令人興奮的」"
              },
              {
                question: "2. I was __________ by the news.",
                options: {
                  A: "surprise",
                  B: "surprising",
                  C: "surprised",
                  D: "surprisingly"
                },
                answer: "C",
                explanation: "surprised 是過去分詞，表示「感到驚訝的」"
              }
            ]
          },
          {
            name: "分詞練習 2",
            questions: [
              {
                question: "1. The __________ child ran to his mother.",
                options: {
                  A: "cry",
                  B: "crying",
                  C: "cried",
                  D: "cries"
                },
                answer: "B",
                explanation: "crying 是現在分詞，修飾 child"
              },
              {
                question: "2. The __________ book was found under the bed.",
                options: {
                  A: "lose",
                  B: "losing",
                  C: "lost",
                  D: "loses"
                },
                answer: "C",
                explanation: "lost 是過去分詞，修飾 book"
              }
            ]
          }
        ]
      }
    }
  }
];

// Export individual units for specific access
export const getUnitById = (unitId) => {
  return grammarUnits10Data.find(unit => unit.id === unitId);
};

// Export units by difficulty
export const getUnitsByDifficulty = (difficulty) => {
  return grammarUnits10Data.filter(unit => unit.difficulty === difficulty);
};

// Export completed units
export const getCompletedUnits = () => {
  return grammarUnits10Data.filter(unit => unit.status === 'completed');
};

// Default export
export default grammarUnits10Data;
